import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { siteBuilder } from '@/mixins/site-builder';
import { guid } from '@/mixins/guid';
import dropsLoader from '@/mixins/components/drops-loader';
import { queuedWork } from '@/mixins/queued-work';
import tooltip from '@/mixins/components/tooltip';

export default Vue.component('save-options', {
  name: 'save-options',
  template: require('./template.html'),
  mixins: [
    siteBuilder,
    guid,
    queuedWork,
  ],
  computed: {
    ...mapGetters([
      'currentPage',
      'currentSite',
      'previewOptions',
      'initialCurrentPage',
    ]),
    rawType() {
      return this.currentPage.type
        ? this.currentPage.type.split('.')[0] || this.currentPage.type
        : null;
    },
    isFullPage() {
      return this._pageTypes.includes(this.rawType);
    },
    isDroplet() {
      return this.type ? this.type.match(/droplet|puddle|section/gi) : false;
    },
    publishTip() {
      let tip = '';
      if (!this._published) {
        tip = 'Option not available until site is live.';
      }
      if (this.$demo) {
        tip = 'Disabled for demo.';
      }
      return tip;
    },
  },
  components: {
    dropsLoader,
    tooltip,
  },
  data() {
    return {
      loaders: {
        save: false,
        draft: false,
        publish: false,
        publishDraft: false,
        droplet: false,
      },
    };
  },
  props: ['type'],
  methods: {
    async runSaveOption(actions) {
      if (!this._published && actions.includes('publish')) {
        return;
      }
      let loader = 'save';
      if (actions.includes('publish')) {
        loader = 'publish';
      }
      this.loaders[loader] = true;
      let msgtype = 'success';
      let message = `${this.currentPage.name} updated successfully!`;
      try {
        const { type, status } = this.currentPage;
        const template = type === 'template';
        const page = type === 'page';
        let queued;
        if (actions.includes('save')) {
          const { _id, forms, lifecycle } = this.currentSite;
          // since dynamic pages are elsewhere, need to condition them
          if (type === 'dynamic page') {
            await this.$lightning([{
              name: 'updateSiteAttr',
              payload: {
                siteId: this.currentSite._id,
                attr: 'dynamic',
                value: this.currentSite.dynamic,
              },
            }]);
          } else {
            await this.$lightning([{
              name: 'savePageItem',
              payload: {
                item: this.currentPage,
                site: { _id, forms },
              },
            }]);
          }
          if (['page', 'template'].includes(type) && status !== 'unpublished') {
            queued = true;
            if (template && !this._published) {
              // when a site is not yet live and published we want any template updates
              // to also update pages using it so they can continue building out their
              // site as they wish before selecting a plan. As publishing a template
              // is not usually allowed when a site is unpublished.
              await this.$store.dispatch('updateTemplatedPages', {
                template: this.currentPage,
              });
            }
          }
          this.$log.debug('Successfully saved changes');
          this.$store.dispatch('updateInitialPage');
          if (lifecycle === 'design' && page) {
            // set in current page only when in design mode / to export better
            this.$store.dispatch('setGlobalValue', {
              item: this.currentPage,
              key: 'status',
              value: 'published',
            });
            this.$store.dispatch('replaceQueuedPage', this.currentPage);
          }
        }

        // finally build site then push to s3
        if (type !== 'draft' && type !== 'dynamic page') {
          // check for any droplets that need s3 content deleted, ie if users
          // remove them from the page and want to save those changes
          await this._runS3Queue();
          if (actions.includes('publish')) {
            // updating template needs to have site updated
            await this.callBuilder({
              publish: template ? 'site' : 'page',
              currentPage: this.currentPage,
              emptyQueue: true,
              updateTemplated: true,
              template,
              site: {
                _id: this.currentSite._id,
              },
              page: {
                _id: this.currentPage._id,
              },
            });
            if (page || template) {
              queued = false;
              const item = this.currentPage;
              this.$store.dispatch('setGlobalValue', {
                item,
                key: 'status',
                value: 'published',
              });
              this.$store.dispatch('replaceQueuedPage', item);
            }
            if (template) {
              // update our pages and drafts in state, that are using
              // this template, with the new template changes
              await this.$store.dispatch('updateTemplatedPages', {
                template: this.currentPage,
              });
            }
          }
        }
        if (queued) {
          this.addToQueuedWork({ page: this.currentPage });
        } else {
          this.removeFromQueuedWork({ page: this.currentPage });
        }
      } catch (e) {
        this.$log.error('error: ', e);
        msgtype = 'error';
        message = `${e.message || e}. This has been reported.`;
        await this._reportError({
          error: e,
          fn: 'runSaveOption',
        });
      } finally {
        this.loaders[loader] = false;
        setTimeout(() => {
          this.$store.dispatch('addNotification', {
            id: this.guid(),
            type: msgtype,
            message,
          });
        }, 500);
        this._trackEvent({
          type: 'save_page',
          meta: {
            save: actions.includes('save'),
            publish: actions.includes('publish'),
            site_name: this.currentSite.alias,
            site_type: this.currentSite.type,
            site_tier: this.currentSite.tier,
            page_name: this.currentPage.name,
            page_url: this.currentPage.url,
            page_type: this.currentPage.type,
            blocks: this.currentPage.blocks.length,
          },
        });
      }
      this.$emit('toggle-options');
    },
    async saveDroplet() {
      this.loaders.droplet = true;
      let msgtype = 'success';
      let message = `${this.currentPage.name} saved successfully!`;
      try {
        await this._SEND([{
          name: 'updateSiteAttr',
          payload: {
            siteId: this.currentSite._id,
            attr: 'recycled',
            value: this.currentSite.recycled,
          },
        }]);
        this.$log.debug('successfully updated recycled');
        this.$store.dispatch('updateInitialPage');
      } catch (e) {
        this.$log.error('error: ', e);
        msgtype = 'error';
        message = `${e.message || e}. This has been reported.`;
        await this._reportError({
          error: e,
          fn: 'save droplet',
        });
      } finally {
        this.loaders.droplet = false;
        setTimeout(() => {
          this.$store.dispatch('addNotification', {
            id: this.guid(),
            type: msgtype,
            message,
          });
        }, 500);
      }
      this.$emit('toggle-options');
    },
    async publishDraft() {
      if (!this._published) {
        return;
      }
      this.loaders.publishDraft = true;
      const draft = _.cloneDeep(this.currentPage);
      const originalPage = _.find(this.currentSite.pages, {
        _id: this.currentPage.pageID,
      });
      const original = _.cloneDeep(originalPage);
      const mergedPage = _.assign({}, original, {
        blocks: draft.blocks,
      });
      await this.setCountDroplets(mergedPage, original);
      // go and save the new page with the draft changes in the db
      let type = 'success';
      let message = `${draft.name} published successfully!`;
      if (originalPage) {
        try {
          // set merged page the current page so the _saveAll can replace it
          this.$store.dispatch('setOriginalPage', mergedPage);
          await this.$store.dispatch('setCurrentPage', mergedPage);
          // delete the draft from state
          const { drafts, forms, _id } = this.currentSite;
          await this.$store.dispatch('removePage', draft);
          await this.$lightning([{
            name: 'publishDraft',
            payload: {
              draft,
              mergedPage,
              drafts,
              forms,
              siteId: _id,
            },
          }]);
          this.$log.debug('Successfully published draft');

          this.$store.dispatch('updateInitialPage');

          // finally build page then send it to S3
          await this.callBuilder({
            publish: 'page',
            emptyQueue: true,
            currentPage: mergedPage,
            site: { _id },
            page: {
              _id: mergedPage._id,
            },
          });
          this.$store.dispatch('setGlobalValue', {
            item: this.currentPage,
            key: 'status',
            value: 'published',
          });
          this.removeFromQueuedWork({ page: this.currentPage });
          this.$log.debug('successfully published draft');
        } catch (e) {
          this.$log.error('error: ', e);
          type = 'error';
          message = `${e.message || e}. This has been reported.`;
          await this._reportError({
            error: e,
            fn: 'publish draft',
          });
        }
      } else {
        // in this scenerio, a user is trying to publish a draft when the
        // original page was deleted from the pages array
        try {
          await this.$store.dispatch('removePage', draft);
          draft.type = 'page';
          draft.pageID = false;
          this.$store.dispatch('addPage', draft);
          this.$store.dispatch('setOriginalPage', draft);
          this.$store.dispatch('setCurrentPage', draft);
          this.$store.dispatch('addToSiteMap', draft);
          await this._SEND([{
            name: 'publishDraft',
            payload: {
              draft,
              mergedPage: draft,
              forms: this.currentSite.forms,
              drafts: this.currentSite.drafts,
              siteId: this.currentSite._id,
              siteMap: this.currentSite.siteMap,
            },
          }]);
          this.$log.debug('Successfully published draft');

          // build page then send it to S3
          await this.callBuilder({
            publish: 'page',
            currentPage: draft,
            site: {
              _id: this.currentSite._id,
            },
            page: {
              _id: draft._id,
            },
          });
          this.$log.debug('successfully published draft of deleted page');
          this.$store.dispatch('updateInitialPage');
        } catch (e) {
          type = 'error';
          message = `${e.message || e}. This has been reported.`;
          await this._reportError({
            error: e,
            fn: 'publish draft',
          });
        }
      }
      this.loaders.publishDraft = false;
      setTimeout(() => {
        this.$store.dispatch('addNotification', {
          id: this.guid(),
          type,
          message,
        });
      }, 500);
      this.$emit('toggle-options');
    },
    setCountDroplets(mergedPage, originalPage) {
      return new Promise((resolve) => {
        mergedPage.forms = mergedPage.draftForms || [];
        delete mergedPage.draftForms;
        // add form from new published draft if it's not already in
        // the site list
        const siteForms = this.currentSite.forms;

        if (originalPage && originalPage._id) {
          // remove all original page forms from site count, they will be readded from draft
          if (originalPage.forms) {
            for (let i = originalPage.forms.length - 1; i >= 0; --i) {
              const listed = _.findIndex(siteForms, f => f === originalPage.forms[i]);
              if (listed >= 0) {
                this.$store.dispatch('spliceDropletVal', {
                  index: listed,
                  array: siteForms,
                });
              }
            }
          }
        }

        if (siteForms) {
          _.forEach(mergedPage.forms, (_id) => {
            if (!siteForms.includes(_id)) {
              this.$store.dispatch('pushDropletVal', {
                array: siteForms,
                val: _id,
              });
            }
          });
        }
        resolve();
      });
    },
    // Need to make a new page object so that the database doesn't override
    // the page we're working on.
    // Then we save it to make available for others to reference
    // and we set the current page to the new draft version so that the user
    // doesn't get confused as to what page they're on
    async saveAsDraft() {
      if (!this._limits.drafts.reached) {
        this.loaders.draft = true;
        const draft = _.cloneDeep(this.currentPage);
        draft._id = this.guid();
        draft.createdDate = new Date().toISOString();
        draft.type = 'draft';
        draft.pageID = this.currentPage._id;

        if (!draft.draftForms) {
          draft.draftForms = [];
        }

        let type = 'success';
        let message = `Draft for ${this.currentPage.name} created!`;
        try {
          await this.$store.dispatch('addDraft', draft);
          await this.$store.dispatch('revertDropletCounts', {
            site: this.currentSite,
          });
          // revert page to initial page
          await this.$store.dispatch('revertPage', this.initialCurrentPage);
          // add new draft page / save to db
          const { _id, forms } = this.currentSite;
          await this.$lightning([{
            name: 'savePageItem',
            payload: {
              item: draft,
              site: { _id, forms },
            },
          }]);
          this.$store.dispatch('setOriginalPage', draft);
          await this.$store.dispatch('setCurrentPage', draft);
          this.$log.debug('Successfully added draft');
        } catch (e) {
          this.$log.error('error: ', e);
          type = 'error';
          message = `${e.message || e}. This has been reported.`;
          await this._reportError({
            error: e,
            fn: 'saveAsDraft',
          });
        } finally {
          this.loaders.draft = false;
          setTimeout(() => {
            this.$store.dispatch('addNotification', {
              id: this.guid(),
              type,
              message,
            });
          }, 500);
        }
        this.$emit('toggle-options');
      }
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/top-control-bar/save-options/index.js